import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { createContext, useContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import {
  useGetStoreSpecials,
  useGetStoreSpecialsPage,
} from '@jane/shared-ecomm/data-access';
import type { StoreSpecial } from '@jane/shared/types';

import { useMenu } from '../menuProvider/menuProvider';

export interface SpecialsContextProps {
  fetchNextSpecialsPage: () => void;
  hasNextSpecialsPage: boolean;
  isLoadingStore?: boolean;
  loadingSpecials?: boolean;
  loadingSpecialsCards?: boolean;
  specialIds?: StoreSpecial['id'][];
  specials?: StoreSpecial[];
  specialsCards?: StoreSpecial[];
}

export const SpecialsContext = createContext<SpecialsContextProps>(
  {} as SpecialsContextProps
);

type SpecialsProviderProps = PropsWithChildren<{ fetchSpecialsRow?: boolean }>;

export const SPECIALS_PAGE_LIMIT = 16;

export const SpecialsProvider = ({
  children,
  fetchSpecialsRow = true,
}: SpecialsProviderProps) => {
  const { loading, store } = useMenu();
  const storeId = store?.id;
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);

  const v1AllQuery = useGetStoreSpecials(store?.id, {
    enabled: !useV3 && !!store?.id,
  });
  const v3AllQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!store?.id,
    storeId: store?.id || 0,
  });

  const allSpecials = useMemo(
    () =>
      useV3
        ? ((v3AllQuery.data?.pages.flatMap((page) => page.specials) ??
            []) as unknown as StoreSpecial[])
        : (v1AllQuery.data ?? []),
    [useV3, v1AllQuery.data, v3AllQuery.data?.pages]
  );

  const loadingSpecials = useV3 ? v3AllQuery.isLoading : v1AllQuery.isLoading;

  const specialIds = useMemo(
    () => allSpecials.map((special) => special.id),
    [allSpecials]
  );

  const specialsRowEnabled = Boolean(
    store?.specials_row_configs?.enabled && fetchSpecialsRow
  );

  const v1PageQuery = useGetStoreSpecialsPage(
    storeId,
    { enabled: !useV3 && specialsRowEnabled },
    SPECIALS_PAGE_LIMIT
  );
  const v3PageQuery = useStoreSpecials({
    appMode: 'default',
    enabled: !!useV3 && specialsRowEnabled,
    storeId: storeId || 0,
  });

  const loadingSpecialsCards = useV3
    ? v3PageQuery.isFetching
    : v1PageQuery.isFetching;
  const fetchNextSpecialsPage = useV3
    ? v3PageQuery.fetchNextPage
    : v1PageQuery.fetchNextPage;
  const hasNextSpecialsPage = useV3
    ? v3PageQuery.hasNextPage
    : v1PageQuery.hasNextPage;

  const specialsCards = useMemo(() => {
    if (useV3) {
      return (v3PageQuery.data?.pages?.flatMap((page) => page.specials) ??
        []) as unknown as StoreSpecial[];
    }
    return v1PageQuery.data?.pages?.flatMap((page) => page.specials) ?? [];
  }, [useV3, v3PageQuery.data, v1PageQuery.data]);

  const contextValue = useMemo(
    () => ({
      fetchNextSpecialsPage,
      hasNextSpecialsPage: !!hasNextSpecialsPage,
      isLoadingStore: loading,
      loadingSpecials,
      loadingSpecialsCards,
      specialIds,
      specials: allSpecials,
      specialsCards,
    }),
    [
      allSpecials,
      fetchNextSpecialsPage,
      hasNextSpecialsPage,
      loading,
      loadingSpecials,
      loadingSpecialsCards,
      specialIds,
      specialsCards,
    ]
  );

  return (
    <SpecialsContext.Provider value={contextValue}>
      {children}
    </SpecialsContext.Provider>
  );
};

export const useSpecials = () => {
  const context = useContext(SpecialsContext);

  if (!Object.keys(context).length) {
    throw new Error('useSpecials must be used within a SpecialsProvider');
  }

  return context;
};
